/** Read-only grid of a booking's event delivery logs (new subsystem), each with
 *  a per-notification success/error breakdown.
 *
 *  Every cell is a starting point (bookingLinks): the id opens the log, the
 *  event / log id / status / emitted time search the real /delivery-logs grid
 *  for siblings — on this booking (`participant=<pk>`) or across the operator —
 *  and the notification chips open the notification or search by it. */
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { Box, Chip, Stack } from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';

import type { DeliveryLog } from '@/api/bookings';
import { TARGET_ROW_SX, targetRowClassName } from '@/components/DataGrid/targetRow';
import { utcTitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { METHOD_STATUS, sandboxMethodLabel } from '@/features/delivery-logs/deliveryStatus';
import { formatDate } from '@/utils/format';

import {
  deliveryLogEmittedActions,
  deliveryLogEventActions,
  deliveryLogIdActions,
  deliveryLogLogIdActions,
  deliveryLogNotificationActions,
  deliveryLogStatusActions,
  type DeliveryLogCtx,
} from './bookingLinks';
import { EmptyLine, READONLY_GRID_PROPS, READONLY_GRID_SX } from './common';

const mono = { fontFamily: 'monospace', fontSize: 12 } as const;

/** Last dash-segment of a UUID id (or the id itself); the full id is the popover title. */
function shortId(id: string): string {
  return id.includes('-') ? (id.split('-').pop() ?? id) : id;
}

function NotificationChips({ log, ctx }: { log: DeliveryLog; ctx: DeliveryLogCtx }) {
  if (!log.notifications.length) return <>—</>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
      {log.notifications.map((n, i) => {
        const err = (n.n_error ?? 0) > 0;
        // A sandbox's method (6–8) is an expected outcome: neutral, never green or red.
        const sandbox = err ? null : sandboxMethodLabel(n.status);
        const result =
          n.status === METHOD_STATUS.CAPTURED ? 'captured' : `${n.n_success ?? 0}/${n.n_error ?? 0}`;
        const chip = (
          <Chip
            size="small"
            color={err ? 'error' : sandbox ? 'default' : 'success'}
            variant={err || sandbox ? 'outlined' : 'filled'}
            {...(sandbox && { icon: <ScienceOutlinedIcon sx={{ fontSize: 14 }} /> })}
            label={`${n.notification_id ?? '?'} ${n.delivery_method_name ?? ''}: ${result}`}
          />
        );
        const title = [n.notification_name, sandbox].filter(Boolean).join(' · ') || undefined;
        return n.notification_id ? (
          <ValueActions
            key={n.id ?? i}
            chip
            title={title}
            actions={deliveryLogNotificationActions(n.notification_id, ctx)}
          >
            {chip}
          </ValueActions>
        ) : (
          <Box key={n.id ?? i} component="span" title={title}>
            {chip}
          </Box>
        );
      })}
    </Stack>
  );
}

function buildColumns(ctx: DeliveryLogCtx): GridColDef<DeliveryLog>[] {
  return [
    {
      field: 'id',
      headerName: 'ID',
      width: 110,
      sortable: false,
      renderCell: (p) => (
        <ValueActions actions={deliveryLogIdActions(p.row)} title={p.row.id}>
          <Box component="span" sx={mono}>{shortId(p.row.id)}</Box>
        </ValueActions>
      ),
    },
    {
      field: 'event',
      headerName: 'Event',
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (p) =>
        p.row.event ? (
          <ValueActions actions={deliveryLogEventActions(p.row.event, ctx)}>{p.row.event}</ValueActions>
        ) : (
          '—'
        ),
    },
    {
      field: 'log_id',
      headerName: 'Log ID',
      width: 150,
      sortable: false,
      renderCell: (p) => {
        const text = [p.row.log_id, p.row.log_tag].filter(Boolean).join('/');
        if (!text) return '—';
        return (
          <ValueActions actions={deliveryLogLogIdActions(p.row, ctx)}>
            <span style={mono}>{text}</span>
          </ValueActions>
        );
      },
    },
    {
      field: 'created_ts',
      headerName: 'Emitted',
      width: 170,
      renderCell: (p) =>
        p.row.created_ts ? (
          <ValueActions
            actions={deliveryLogEmittedActions(p.row.created_ts, ctx)}
            title={utcTitle(p.row.created_ts)}
          >
            {formatDate(p.row.created_ts)}
          </ValueActions>
        ) : (
          '—'
        ),
    },
    {
      field: 'duration_ms',
      headerName: 'Duration',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (p.row.duration_ms != null ? `${p.row.duration_ms} ms` : '—'),
    },
    {
      field: 'status_display',
      headerName: 'Status',
      width: 130,
      renderCell: (p) => {
        const label = p.row.status_display || (p.row.status != null ? String(p.row.status) : '');
        if (!label) return '—';
        return <ValueActions actions={deliveryLogStatusActions(label, ctx)}>{label}</ValueActions>;
      },
    },
    {
      field: 'notifications',
      headerName: 'Notifications',
      flex: 1.4,
      minWidth: 220,
      sortable: false,
      renderCell: (p) => <NotificationChips log={p.row} ctx={ctx} />,
    },
  ];
}

export function DeliveryLogsGrid({
  logs,
  ctx,
  highlightId,
}: {
  logs: DeliveryLog[];
  ctx: DeliveryLogCtx;
  /** A row the host points at (the booking's time axis) — tinted. */
  highlightId?: string | null;
}) {
  const columns = useMemo(() => buildColumns(ctx), [ctx]);
  if (!logs.length) return <EmptyLine>No event delivery logs.</EmptyLine>;
  return (
    <DataGridPro
      rows={logs}
      columns={columns}
      getRowId={(r) => r.id}
      initialState={{ sorting: { sortModel: [{ field: 'created_ts', sort: 'desc' }] } }}
      getRowClassName={targetRowClassName(highlightId)}
      sx={{ ...READONLY_GRID_SX, ...TARGET_ROW_SX }}
      {...READONLY_GRID_PROPS}
    />
  );
}
